import React from 'react'
import * as RadixAvatar from '@radix-ui/react-avatar'
import { cn, getInitials } from '@/lib/utils'

type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

interface AvatarProps {
  src?:       string | null
  name?:      string | null
  size?:      AvatarSize
  className?: string
  alt?:       string
}

const sizeClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-2xs',
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
}

// Background colours derived from name hash for visual distinctness
const bgPalette = [
  'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
  'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300',
  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
  'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
  'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-300',
  'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300',
  'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300',
  'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
]

function nameToColor(name: string | null | undefined): string {
  if (!name) return bgPalette[0]
  const hash = [...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  return bgPalette[hash % bgPalette.length]
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  name,
  size  = 'md',
  className,
  alt,
}) => (
  <RadixAvatar.Root
    className={cn(
      'relative inline-flex items-center justify-center rounded-full overflow-hidden flex-shrink-0',
      sizeClasses[size],
      className,
    )}
  >
    {src && (
      <RadixAvatar.Image
        src={src}
        alt={alt ?? name ?? 'Avatar'}
        className="h-full w-full object-cover"
      />
    )}
    <RadixAvatar.Fallback
      delayMs={src ? 200 : 0}
      className={cn(
        'flex h-full w-full items-center justify-center font-semibold select-none',
        nameToColor(name),
      )}
    >
      {getInitials(name)}
    </RadixAvatar.Fallback>
  </RadixAvatar.Root>
)
